// ═══════════════════════════════════════════════════════════════════════════════
// Admin login — standalone, dark theme; email OTP 2FA after password when enabled
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { adminPortalPath } from '../../lib/adminPortalPaths';

export const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { login, resendTwoFactorCode, isAuthenticated, isLoading } = useAdminAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    navigate(adminPortalPath('dashboard'), { replace: true });
  }, [isLoading, isAuthenticated, navigate]);

  const handleSubmitPassword = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError('');
      setInfo('');
      setSubmitting(true);
      try {
        const result = await login(email.trim(), password);
        if (result.status === 'requires_two_factor') {
          setOtpCode('');
          setShowOtpModal(true);
          setInfo(t('admin.otpSentHint'));
          return;
        }
        navigate(adminPortalPath('dashboard'), { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : t('auth.errorGeneric'));
      } finally {
        setSubmitting(false);
      }
    },
    [email, password, login, navigate, t]
  );

  const handleSubmitOtp = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError('');
      setInfo('');
      setSubmitting(true);
      try {
        const result = await login(email.trim(), password, otpCode.trim());
        if (result.status === 'requires_two_factor') {
          setError(t('admin.otpRequired'));
          return;
        }
        navigate(adminPortalPath('dashboard'), { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : t('auth.errorGeneric'));
      } finally {
        setSubmitting(false);
      }
    },
    [email, password, otpCode, login, navigate, t]
  );

  const handleResendOtp = useCallback(async () => {
    setError('');
    setInfo('');
    setResending(true);
    try {
      await resendTwoFactorCode(email.trim(), password);
      setInfo(t('admin.otpResentHint'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.errorGeneric'));
    } finally {
      setResending(false);
    }
  }, [email, password, resendTwoFactorCode, t]);

  const handleCloseOtpModal = useCallback(() => {
    setShowOtpModal(false);
    setOtpCode('');
    setError('');
    setInfo('');
  }, []);

  if (isLoading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-100"
        role="status"
        aria-label={t('common.loading')}
      >
        <p className="text-sm">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-12 text-slate-100">
      <div className="mx-auto w-full max-w-md">
        <header className="mb-8 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-amber-400/90">
            {t('admin.brand')}
          </p>
          <h1 className="mt-2 text-2xl font-semibold">{t('admin.loginTitle')}</h1>
          <p className="mt-2 text-sm text-slate-400">{t('admin.loginSubtitle')}</p>
        </header>

        <form
          onSubmit={(e) => void handleSubmitPassword(e)}
          className="rounded-xl border border-slate-800 bg-slate-900/80 p-6 shadow-xl"
          noValidate
        >
          <div className="space-y-4">
            <div>
              <label htmlFor="admin-email" className="mb-1 block text-sm text-slate-300">
                {t('admin.email')}
              </label>
              <input
                id="admin-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white outline-none ring-amber-500/50 focus:ring-2"
                required
                aria-required="true"
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="mb-1 block text-sm text-slate-300">
                {t('admin.password')}
              </label>
              <input
                id="admin-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(ev) => setPassword(ev.target.value)}
                className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white outline-none ring-amber-500/50 focus:ring-2"
                required
                aria-required="true"
              />
            </div>
          </div>

          {error && !showOtpModal ? (
            <p className="mt-4 text-sm text-red-400" role="alert">
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={submitting}
            className="mt-6 w-full rounded-md bg-amber-500 px-4 py-2.5 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60"
            aria-busy={submitting}
          >
            {submitting ? t('admin.signingIn') : t('admin.signIn')}
          </button>
        </form>
      </div>

      {showOtpModal ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="admin-otp-title"
        >
          <div className="w-full max-w-md rounded-xl border border-slate-700 bg-slate-900 p-6 shadow-2xl">
            <h2 id="admin-otp-title" className="text-lg font-semibold text-white">
              {t('admin.otpModalTitle')}
            </h2>
            <p className="mt-2 text-sm text-slate-400">{t('admin.otpModalHint')}</p>

            <form onSubmit={(e) => void handleSubmitOtp(e)} className="mt-4" noValidate>
              <label htmlFor="admin-otp" className="mb-1 block text-sm text-slate-300">
                {t('admin.otpCode')}
              </label>
              <input
                id="admin-otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                value={otpCode}
                onChange={(ev) => setOtpCode(ev.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white outline-none ring-amber-500/50 focus:ring-2"
                placeholder="000000"
                aria-required="true"
                autoFocus
              />

              {info ? (
                <p className="mt-3 text-sm text-emerald-400" role="status">
                  {info}
                </p>
              ) : null}
              {error ? (
                <p className="mt-3 text-sm text-red-400" role="alert">
                  {error}
                </p>
              ) : null}

              <div className="mt-5 flex flex-col gap-2 sm:flex-row">
                <button
                  type="submit"
                  disabled={submitting || otpCode.length !== 6}
                  className="flex-1 rounded-md bg-amber-500 px-4 py-2.5 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60"
                  aria-busy={submitting}
                >
                  {submitting ? t('admin.signingIn') : t('admin.verifyOtp')}
                </button>
                <button
                  type="button"
                  disabled={resending}
                  className="flex-1 rounded-md border border-slate-600 px-4 py-2.5 text-sm text-slate-200 hover:bg-slate-800 disabled:opacity-60"
                  onClick={() => void handleResendOtp()}
                  aria-busy={resending}
                >
                  {resending ? t('common.loading') : t('admin.resendOtp')}
                </button>
              </div>
              <button
                type="button"
                className="mt-3 w-full rounded-md px-4 py-2 text-sm text-slate-400 hover:text-slate-200"
                onClick={handleCloseOtpModal}
              >
                {t('common.cancel')}
              </button>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};
